import { Temporal } from '@js-temporal/polyfill'
import type { components } from '@joyful/openapi/types.gen'
import { toPlainDate } from './date'
import { formatDate } from './format'
import type { SetNonNullableRequired } from './types'
import { createEnumSchema } from './zod'
import type { EnumValueMeta } from './zod'
import type { SetRequired } from 'type-fest'
import { z } from 'zod'

type EncounterAPIResponse = components['schemas']['EncounterAPIResponse']

interface WorkflowStatusMetadata extends SetRequired<
  EnumValueMeta,
  'description'
> {
  variant: EncounterWorkflowStatusVariant
}

interface PayerPriorityMetadata extends EnumValueMeta {
  variant: 'info' | 'success' | 'warning'
}

export type EncounterWorkflowStatusVariant =
  | 'brand'
  | 'failure'
  | 'info'
  | 'inverse'
  | 'neutral'
  | 'resolved'
  | 'success'

export type EncounterWorkflowStatus =
  components['schemas']['EncounterWorkflowStatus']

export type UpdateEligibleEncounterWorkflowStatus = Exclude<
  EncounterWorkflowStatus,
  | 'RESOLUTION_RESOLVED'
  | 'RESOLUTION_PAYMENT_POSTING_ONLY'
  | 'RESOLUTION_UNCOLLECTIBLE'
  | 'RESOLUTION_NO_ACTION_REQUIRED'
  | 'CLOSED_RESOLVED'
  | 'CLOSED_PAYMENT_POSTING_ONLY'
  | 'CLOSED_UNCOLLECTIBLE'
  | 'CLOSED_NO_ACTION_REQUIRED'
>

export type EncounterWorkflowStatusSimplified =
  components['schemas']['EncounterWorkflowStatusSimplified']

export type AnyEncounterWorkflowStatus =
  | EncounterWorkflowStatus
  | EncounterWorkflowStatusSimplified

export type EncounterWorkflowDisplayState =
  components['schemas']['EncounterWorkflowDisplayState']

export type EncounterWorkflowStatusDisplay =
  components['schemas']['EncounterWorkflowStatusDisplay']

interface EncounterWorkflowDisplayStateMetadata extends EnumValueMeta {
  variant: EncounterWorkflowStatusVariant
}

export const NewWorkflowStatusSchema = createEnumSchema<
  Extract<EncounterWorkflowStatus, 'NEW'>,
  WorkflowStatusMetadata
>([
  {
    description:
      "The claim is in Joyful's queue but has not yet been touched by a biller.",
    title: 'In Queue',
    value: 'NEW',
    variant: 'neutral',
  },
])

export const InProgressWorkflowStatusSchema = createEnumSchema<
  Extract<
    EncounterWorkflowStatus,
    | 'IN_PROGRESS_REVIEWING'
    | 'IN_PROGRESS_AWAITING_PAYER_CLARIFICATION'
    | 'IN_PROGRESS_AWAITING_PRACTICE_RESPONSE'
    | 'IN_PROGRESS_ESCALATE_TO_BILLER'
    | 'IN_PROGRESS_ESCALATE_TO_JOYFUL'
    | 'IN_PROGRESS_REVIEWED_RETURNED_TO_BILLER'
    | 'IN_PROGRESS_SUBMIT_CLAIM_TO_SECONDARY'
  >,
  WorkflowStatusMetadata
>([
  {
    description: 'Active review and research of the claim is underway.',
    title: 'In progress - Reviewing',
    value: 'IN_PROGRESS_REVIEWING',
    variant: 'brand',
  },
  {
    description: 'Claim review paused while waiting for payer response.',
    title: 'In progress - Awaiting payer clarification',
    value: 'IN_PROGRESS_AWAITING_PAYER_CLARIFICATION',
    variant: 'brand',
  },
  {
    description: 'Claim review paused while waiting for practice response.',
    title: 'In progress - Awaiting practice response',
    value: 'IN_PROGRESS_AWAITING_PRACTICE_RESPONSE',
    variant: 'brand',
  },
  {
    description: 'Claim escalated to billing supervisor.',
    title: 'In progress - Escalate to supervisor',
    value: 'IN_PROGRESS_ESCALATE_TO_BILLER',
    variant: 'brand',
  },
  {
    description:
      'Claim escalated to Joyful Health team (supervisor cannot resolve).',
    title: 'In progress - Escalate to Joyful',
    value: 'IN_PROGRESS_ESCALATE_TO_JOYFUL',
    variant: 'brand',
  },
  {
    description:
      'Claim reviewed by supervisor / Joyful and returned to biller after resolving issue.',
    title: 'In progress - Reviewed, returned to biller',
    value: 'IN_PROGRESS_REVIEWED_RETURNED_TO_BILLER',
    variant: 'brand',
  },
  {
    description:
      'Claim needs submission to secondary payer after primary adjudication.',
    title: 'In progress - Submit claim to secondary',
    value: 'IN_PROGRESS_SUBMIT_CLAIM_TO_SECONDARY',
    variant: 'brand',
  },
])

export const PendingResolutionWorkflowStatusSchema = createEnumSchema<
  Extract<
    EncounterWorkflowStatus,
    | 'PENDING_RESOLUTION_CORRECTED_REJECTION'
    | 'PENDING_RESOLUTION_PAYER_PROCESSING_DELAYS'
    | 'PENDING_RESOLUTION_RESUBMITTED'
    | 'PENDING_RESOLUTION_SUBMITTED_DOCUMENTATION'
    | 'PENDING_RESOLUTION_UNDER_APPEAL'
    | 'PENDING_RESOLUTION_UNDER_BATCH_PROCESSING'
  >,
  WorkflowStatusMetadata
>([
  {
    description:
      'This claim was rejected and it was resubmitted. It is now pending adjudication.',
    title: 'Pending resolution - Corrected rejection',
    value: 'PENDING_RESOLUTION_CORRECTED_REJECTION',
    variant: 'info',
  },
  {
    description: 'Claim is delayed due to payer backlog.',
    title: 'Pending resolution - Payer processing delays',
    value: 'PENDING_RESOLUTION_PAYER_PROCESSING_DELAYS',
    variant: 'info',
  },
  {
    description:
      'Claim has been corrected and resubmitted to payer. It is now pending adjudication.',
    title: 'Pending resolution - Resubmitted',
    value: 'PENDING_RESOLUTION_RESUBMITTED',
    variant: 'info',
  },
  {
    description:
      'Supporting documentation was submitted for this claim to the payer. It is now pending adjudication.',
    title: 'Pending resolution - Submitted documentation',
    value: 'PENDING_RESOLUTION_SUBMITTED_DOCUMENTATION',
    variant: 'info',
  },
  {
    description: 'Appeal has been submitted for the claim.',
    title: 'Pending resolution - Under appeal',
    value: 'PENDING_RESOLUTION_UNDER_APPEAL',
    variant: 'info',
  },
  {
    description: 'Claim is under batch processing with the payer.',
    title: 'Pending resolution - Under batch processing',
    value: 'PENDING_RESOLUTION_UNDER_BATCH_PROCESSING',
    variant: 'info',
  },
])

export const ResolutionWorkflowStatusSchema = createEnumSchema<
  Extract<
    EncounterWorkflowStatus,
    | 'RESOLUTION_RESOLVED'
    | 'RESOLUTION_PAYMENT_POSTING_ONLY'
    | 'RESOLUTION_UNCOLLECTIBLE'
    | 'RESOLUTION_NO_ACTION_REQUIRED'
  >,
  WorkflowStatusMetadata
>([
  {
    description:
      'Our team has successfully worked this claim and recovered revenue (via a recovered denial, rejection, or found payment).',
    title: 'Resolution - Resolved',
    value: 'RESOLUTION_RESOLVED',
    variant: 'brand',
  },
  {
    description:
      "We found the payment for this claim in the practice's EHR or Clearinghouse, and all we did was post the payment.",
    title: 'Resolution - Payment posting only',
    value: 'RESOLUTION_PAYMENT_POSTING_ONLY',
    variant: 'brand',
  },
  {
    description:
      'We took every attempt, but this claim is uncollectible from insurance.',
    title: 'Resolution - Uncollectible',
    value: 'RESOLUTION_UNCOLLECTIBLE',
    variant: 'brand',
  },
  {
    description:
      'We did not need to take any action to recover this claim. It was already handled by the practice before we began work on it.',
    title: 'Resolution - No action required',
    value: 'RESOLUTION_NO_ACTION_REQUIRED',
    variant: 'brand',
  },
])

export const ClosedWorkflowStatusSchema = createEnumSchema<
  Extract<
    EncounterWorkflowStatus,
    | 'CLOSED_RESOLVED'
    | 'CLOSED_PAYMENT_POSTING_ONLY'
    | 'CLOSED_UNCOLLECTIBLE'
    | 'CLOSED_NO_ACTION_REQUIRED'
  >,
  WorkflowStatusMetadata
>([
  {
    description:
      "You got paid because of Joyful's work. Our percentage fee will apply to these claims.",
    title: 'Closed - Recovered',
    value: 'CLOSED_RESOLVED',
    variant: 'success',
  },
  {
    description: 'Payment existed; Joyful helped reflect it correctly.',
    title: 'Closed - Payment Posting Only',
    value: 'CLOSED_PAYMENT_POSTING_ONLY',
    variant: 'success',
  },
  {
    description: "We investigated on your behalf, but insurance won't pay.",
    title: 'Closed - Uncollectible',
    value: 'CLOSED_UNCOLLECTIBLE',
    variant: 'success',
  },
  {
    description:
      'This claim did not require action from Joyful and was excluded from recovery metrics and billing.',
    title: 'No Action Required',
    value: 'CLOSED_NO_ACTION_REQUIRED',
    variant: 'inverse',
  },
])

export const WorkflowStatusSchema = createEnumSchema<
  EncounterWorkflowStatus,
  WorkflowStatusMetadata
>([
  ...NewWorkflowStatusSchema.definitions,
  ...InProgressWorkflowStatusSchema.definitions,
  ...PendingResolutionWorkflowStatusSchema.definitions,
  ...ResolutionWorkflowStatusSchema.definitions,
  ...ClosedWorkflowStatusSchema.definitions,
])

export const UpdateEligibleWorkflowStatusSchema = createEnumSchema<
  UpdateEligibleEncounterWorkflowStatus,
  WorkflowStatusMetadata
>([
  ...NewWorkflowStatusSchema.definitions,
  ...InProgressWorkflowStatusSchema.definitions,
  ...PendingResolutionWorkflowStatusSchema.definitions,
])

export const WorkflowStatusSimplifiedSchema = createEnumSchema<
  EncounterWorkflowStatusSimplified,
  WorkflowStatusMetadata
>([
  {
    description:
      'Joyful has received this claim and it is queued to be worked.',
    title: 'In Queue',
    value: 'IN_QUEUE',
    variant: 'neutral',
  },
  {
    description:
      'Joyful is actively reviewing and working to resolve this claim.',
    title: 'In Progress',
    value: 'IN_PROGRESS',
    variant: 'brand',
  },
  {
    description:
      'Joyful is waiting on information from your team to move this claim forward.',
    title: 'Pending Practice',
    value: 'PENDING_PRACTICE',
    variant: 'brand',
  },
  {
    description:
      'Joyful is waiting on the insurance payer to respond before we can proceed.',
    title: 'Pending Payer',
    value: 'PENDING_PAYER',
    variant: 'info',
  },
  {
    description:
      "You got paid because of Joyful's work. Our percentage fee will apply to these claims.",
    title: 'Closed - Recovered',
    value: 'CLOSED_RECOVERED',
    variant: 'success',
  },
  {
    description: "We investigated on your behalf, but insurance won't pay.",
    title: 'Closed - Uncollectible',
    value: 'CLOSED_UNCOLLECTIBLE',
    variant: 'success',
  },
  {
    description: 'Payment existed; Joyful helped reflect it correctly.',
    title: 'Closed - Payment Posting Only',
    value: 'CLOSED_PAYMENT_POSTING_ONLY',
    variant: 'success',
  },
])

export const AnyWorkflowStatusSchema = createEnumSchema<
  AnyEncounterWorkflowStatus,
  WorkflowStatusMetadata
>([
  ...WorkflowStatusSchema.definitions,
  ...WorkflowStatusSimplifiedSchema.definitions,
])

export const PayerPrioritySchema = createEnumSchema<
  components['schemas']['PayerPriority'],
  PayerPriorityMetadata
>([
  { title: 'Primary', value: 'PRIMARY', variant: 'success' },
  { title: 'Secondary', value: 'SECONDARY', variant: 'warning' },
  { title: 'Tertiary', value: 'TERTIARY', variant: 'info' },
])

export const EncounterWorkflowDisplayStateSchema = createEnumSchema<
  EncounterWorkflowDisplayState,
  EncounterWorkflowDisplayStateMetadata
>([
  { title: 'In queue', value: 'NEW', variant: 'neutral' },
  { title: 'In progress', value: 'IN_PROGRESS', variant: 'brand' },
  { title: 'Pending practice', value: 'PENDING_PRACTICE', variant: 'brand' },
  { title: 'Pending payer', value: 'PENDING_PAYER', variant: 'info' },
  { title: 'Pending resolution', value: 'PENDING_RESOLUTION', variant: 'info' },
  { title: 'Resolved', value: 'RESOLUTION', variant: 'resolved' },
  { title: 'Closed', value: 'CLOSED', variant: 'success' },
  {
    title: 'No action required',
    value: 'NO_ACTION_REQUIRED',
    variant: 'inverse',
  },
])

const EncounterWorkflowStatusDisplaySchema = z.object({
  reason: z.string().nullable().optional(),
  state: EncounterWorkflowDisplayStateSchema.schema,
})

export function formatWorkflowStatusDisplayTitle(
  display: EncounterWorkflowStatusDisplay,
) {
  const status = EncounterWorkflowDisplayStateSchema.meta(display.state)
  return display.reason ? `${status.title}: ${display.reason}` : status.title
}

const WORKFLOW_STATUS_REASONS: Partial<
  Record<EncounterWorkflowStatus, string>
> = {
  CLOSED_PAYMENT_POSTING_ONLY: 'Payment posting only',
  CLOSED_RESOLVED: 'Recovered',
  CLOSED_UNCOLLECTIBLE: 'Uncollectible',
  IN_PROGRESS_AWAITING_PAYER_CLARIFICATION: 'Awaiting payer clarification',
  IN_PROGRESS_AWAITING_PRACTICE_RESPONSE: 'Awaiting practice response',
  IN_PROGRESS_ESCALATE_TO_BILLER: 'Escalate to supervisor',
  IN_PROGRESS_ESCALATE_TO_JOYFUL: 'Escalate to Joyful',
  IN_PROGRESS_REVIEWED_RETURNED_TO_BILLER: 'Reviewed, returned to biller',
  IN_PROGRESS_REVIEWING: 'Reviewing',
  IN_PROGRESS_SUBMIT_CLAIM_TO_SECONDARY: 'Submit claim to secondary',
  PENDING_RESOLUTION_CORRECTED_REJECTION: 'Corrected rejection',
  PENDING_RESOLUTION_PAYER_PROCESSING_DELAYS: 'Payer processing delays',
  PENDING_RESOLUTION_RESUBMITTED: 'Resubmitted',
  PENDING_RESOLUTION_SUBMITTED_DOCUMENTATION: 'Submitted documentation',
  PENDING_RESOLUTION_UNDER_APPEAL: 'Under appeal',
  PENDING_RESOLUTION_UNDER_BATCH_PROCESSING: 'Under batch processing',
  RESOLUTION_NO_ACTION_REQUIRED: 'No action required',
  RESOLUTION_PAYMENT_POSTING_ONLY: 'Payment posting only',
  RESOLUTION_RESOLVED: 'Recovered',
  RESOLUTION_UNCOLLECTIBLE: 'Uncollectible',
}

const SIMPLIFIED_TO_FULL_DISPLAY_STATUS: Partial<
  Record<EncounterWorkflowStatusSimplified, EncounterWorkflowStatus>
> = {
  CLOSED_PAYMENT_POSTING_ONLY: 'CLOSED_PAYMENT_POSTING_ONLY',
  CLOSED_RECOVERED: 'CLOSED_RESOLVED',
  CLOSED_UNCOLLECTIBLE: 'CLOSED_UNCOLLECTIBLE',
}

const SIMPLIFIED_WORKFLOW_DISPLAY_STATES: Partial<
  Record<EncounterWorkflowStatusSimplified, EncounterWorkflowDisplayState>
> = {
  IN_PROGRESS: 'IN_PROGRESS',
  IN_QUEUE: 'NEW',
  PENDING_PAYER: 'PENDING_PAYER',
  PENDING_PRACTICE: 'PENDING_PRACTICE',
}

function getWorkflowStatusDisplay(
  workflowStatus: EncounterWorkflowStatus,
): EncounterWorkflowStatusDisplay {
  return {
    reason: WORKFLOW_STATUS_REASONS[workflowStatus],
    state: getWorkflowDisplayState(workflowStatus),
  }
}

function getSimplifiedWorkflowStatusDisplay(
  workflowStatus: EncounterWorkflowStatusSimplified,
): EncounterWorkflowStatusDisplay {
  const fullWorkflowStatus = SIMPLIFIED_TO_FULL_DISPLAY_STATUS[workflowStatus]
  if (fullWorkflowStatus) {
    return getWorkflowStatusDisplay(fullWorkflowStatus)
  }

  const state = SIMPLIFIED_WORKFLOW_DISPLAY_STATES[workflowStatus]
  if (state) {
    return { state }
  }

  throw new Error(`Unsupported simplified workflow status: ${workflowStatus}`)
}

export function resolveEncounterWorkflowStatusTagDisplay(
  workflowStatus: AnyEncounterWorkflowStatus,
  workflowStatusDisplay?: EncounterWorkflowStatusDisplay | null,
): EncounterWorkflowStatusDisplay {
  if (workflowStatusDisplay) {
    const displayParse = EncounterWorkflowStatusDisplaySchema.safeParse(
      workflowStatusDisplay,
    )
    if (displayParse.success) {
      return displayParse.data
    }
  }

  const simplifiedParse =
    WorkflowStatusSimplifiedSchema.schema.safeParse(workflowStatus)
  if (simplifiedParse.success) {
    return getSimplifiedWorkflowStatusDisplay(simplifiedParse.data)
  }

  return getWorkflowStatusDisplay(
    WorkflowStatusSchema.schema.parse(workflowStatus),
  )
}

function getWorkflowDisplayState(
  workflowStatus: EncounterWorkflowStatus,
): EncounterWorkflowDisplayState {
  if (workflowStatus === 'NEW') {
    return 'NEW'
  }

  if (workflowStatus === 'CLOSED_NO_ACTION_REQUIRED') {
    return 'NO_ACTION_REQUIRED'
  }

  const inProgressParse =
    InProgressWorkflowStatusSchema.schema.safeParse(workflowStatus)
  if (inProgressParse.success) {
    return 'IN_PROGRESS'
  }

  const pendingResolutionParse =
    PendingResolutionWorkflowStatusSchema.schema.safeParse(workflowStatus)
  if (pendingResolutionParse.success) {
    return 'PENDING_RESOLUTION'
  }

  const resolutionParse =
    ResolutionWorkflowStatusSchema.schema.safeParse(workflowStatus)
  if (resolutionParse.success) {
    return 'RESOLUTION'
  }

  const closedParse =
    ClosedWorkflowStatusSchema.schema.safeParse(workflowStatus)
  if (closedParse.success) {
    return 'CLOSED'
  }

  throw new Error(`Unsupported workflow status: ${workflowStatus}`)
}

const AwaitingPayerResponseStatuses = new Set<
  components['schemas']['EncounterEnrichmentStatus']
>([
  'AWAITING_PAYER_RESPONSE',
  'AWAITING_PRIMARY_PAYER_RESPONSE',
  'AWAITING_SECONDARY_PAYER_RESPONSE',
  'AWAITING_TERTIARY_PAYER_RESPONSE',
])

function isEncounterAwaitingPayerResponse(
  encounter: Pick<
    EncounterAPIResponse,
    'enrichment_status' | 'enrichment_status_override'
  >,
) {
  const resolvedEnrichmentStatus =
    encounter.enrichment_status_override ?? encounter.enrichment_status
  return (
    resolvedEnrichmentStatus !== null &&
    resolvedEnrichmentStatus !== undefined &&
    AwaitingPayerResponseStatuses.has(resolvedEnrichmentStatus)
  )
}

export function isEncounterMayHaveManuallyPostedPayment(
  encounter: Pick<
    EncounterAPIResponse,
    'workflow_status' | 'enrichment_status' | 'enrichment_status_override'
  >,
) {
  return (
    encounter.workflow_status === 'NEW' &&
    isEncounterAwaitingPayerResponse(encounter)
  )
}

export function isEncounterEscalated(
  encounter: Pick<EncounterAPIResponse, 'workflow_status'>,
) {
  return (
    // oxlint-disable-next-line joyful/prefer-boolean-check
    encounter.workflow_status?.startsWith('IN_PROGRESS_ESCALATE_TO_') === true
  )
}

export function isResolutionWorkflowStatus(
  workflowStatus: EncounterWorkflowStatus | null | undefined,
) {
  // oxlint-disable-next-line joyful/prefer-boolean-check
  return workflowStatus?.startsWith('RESOLUTION_') === true
}

export function isClosedWorkflowStatus(
  workflowStatus: EncounterWorkflowStatus | null | undefined,
) {
  // oxlint-disable-next-line joyful/prefer-boolean-check
  return workflowStatus?.startsWith('CLOSED') === true
}

export function isEncounterResolved(
  encounter: Pick<EncounterAPIResponse, 'workflow_status'>,
) {
  return isResolutionWorkflowStatus(encounter.workflow_status)
}

export function isEncounterClosed(
  encounter: Pick<
    EncounterAPIResponse,
    'workflow_status' | 'workflow_status_simplified'
  >,
) {
  const workflowStatus =
    encounter.workflow_status ?? encounter.workflow_status_simplified
  // oxlint-disable-next-line joyful/prefer-boolean-check
  return workflowStatus?.startsWith('CLOSED') === true
}

export function isEncounterFlaggedForFollowUp(
  encounter: Pick<EncounterAPIResponse, 'flagged_for_joyful_followup_at'>,
) {
  return typeof encounter.flagged_for_joyful_followup_at === 'string'
}

export function isEncounterRejected(
  encounter: Pick<
    EncounterAPIResponse,
    'enrichment_status' | 'enrichment_status_override'
  >,
) {
  const resolvedEnrichmentStatus =
    encounter.enrichment_status_override ?? encounter.enrichment_status
  return resolvedEnrichmentStatus === 'REJECTED'
}

function parseEncounterSnoozeDate(
  encounter: Pick<EncounterAPIResponse, 'snoozed_until'>,
): Temporal.PlainDate | null {
  const plainDate = toPlainDate(encounter.snoozed_until)
  if (plainDate === null) {
    return null
  }

  return plainDate
}

type EncounterSnoozeFields = Pick<
  EncounterAPIResponse,
  'snoozed_until' | 'snoozed_until_is_indefinite'
>

export function isEncounterSnoozed(
  encounter: EncounterSnoozeFields,
): encounter is SetNonNullableRequired<EncounterSnoozeFields, 'snoozed_until'> {
  if (encounter.snoozed_until_is_indefinite) {
    return true
  }

  const snoozeDate = parseEncounterSnoozeDate(encounter)
  if (snoozeDate === null) {
    return false
  }

  const today = Temporal.Now.plainDateISO('America/New_York')
  return Temporal.PlainDate.compare(today, snoozeDate) < 0
}

export function isEncounterSnoozeExpired(
  encounter: EncounterSnoozeFields,
): encounter is SetNonNullableRequired<EncounterSnoozeFields, 'snoozed_until'> {
  if (encounter.snoozed_until_is_indefinite) {
    return false
  }

  const snoozeDate = parseEncounterSnoozeDate(encounter)
  if (snoozeDate === null) {
    return false
  }

  const today = Temporal.Now.plainDateISO('America/New_York')
  return Temporal.PlainDate.compare(today, snoozeDate) >= 0
}

export function collectDistinctBillingProviderTaxIds(
  encounter: Pick<EncounterAPIResponse, 'claims'>,
) {
  const taxIds = new Set<string>()

  for (const claim of encounter.claims) {
    if (claim.billing_provider_tax_id) {
      taxIds.add(claim.billing_provider_tax_id)
    }
  }

  return Array.from(taxIds)
}

export function formatEncounterServiceDate(
  encounter: Pick<EncounterAPIResponse, 'start_date' | 'end_date'>,
) {
  return [
    formatDate(encounter.start_date, {
      format: 'numeric',
    }),
    encounter.end_date &&
      encounter.end_date !== encounter.start_date &&
      formatDate(encounter.end_date, {
        format: 'numeric',
      }),
  ]
    .filter(Boolean)
    .join(' - ')
}

export function formatEncounterLabel({
  patient_name,
  start_date,
}: {
  patient_name?: string | null
  start_date?: string | null
}) {
  const patientName = patient_name?.trim() || 'Unknown patient'
  if (!start_date) {
    return patientName
  }

  return `${patientName} (${formatDate(start_date, { format: 'numeric' })})`
}
